import { Box, Chip, CircularProgress, Divider, Stack, Tab, Tabs } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ko';
import DateRangeSelector from '../../../../components/DateRangeSelector';
import { PAGE_CARD_SX } from '../../../../theme/pageCardSx';
import FilterSelectorWithApp from '../../../../components/FilterSelectorWithApp';
import FilterSelectorUser, {
  type UserFilterValue,
} from '../../../../components/pointhome/roulette/chart/FilterSelectorUser';
import RouletteContentKpiCards from '../../../../components/pointhome/roulette/content/RouletteContentKpiCards';
import apiClient from '../../../../utils/axiosClient';
import type { UserStatusDatum } from '../../../../components/pointhome/roulette/content/RouletteContentUserStatusChart';
import type { TimeParticipationDatum } from '../../../../components/pointhome/roulette/content/RouletteContentTimeParticipationChart';
import type { TimeParticipationTableRow } from '../../../../components/pointhome/roulette/content/RouletteContentTimeParticipationTable';
import type { DailyCountDatum } from '../../../../components/pointhome/roulette/content/RouletteContentDailyCountChart';
import type { DailyCountTableRow } from '../../../../components/pointhome/roulette/content/RouletteContentDailyCountTable';
import type { TicketAcquisitionDatum } from '../../../../components/pointhome/roulette/content/RouletteContentTicketAcquisitionCharts';
import type { RouletteTabChartDatum } from '../../../../components/pointhome/roulette/content/RouletteContentRouletteTabChart';
import type { RouletteTabTableRow } from '../../../../components/pointhome/roulette/content/RouletteContentRouletteTabTable';
import { ROULETTE_TAB_COLUMNS } from '../../../../components/pointhome/roulette/content/RouletteContentRouletteTabTable';
import TabFirstVisit from './TabFirstVisit';
import type {
  WelcomeAvgParticipationDays,
  WelcomeCta,
  WelcomeFunnel,
  WelcomePerfectAttendanceRate,
  WelcomeRetention,
} from './TabFirstVisit';
import TabGeneral from './TabGeneral';
import TabRoulette from './TabRoulette';

dayjs.locale('ko');

/** 1룰렛(럭키룰렛) = 럭키룰렛 매핑 */
const ROULETTE_BACKEND_KEY_ALIASES: Record<string, string[]> = {
  '1룰렛(럭키룰렛)': ['럭키룰렛'],
};

function getRouletteCount(row: Record<string, unknown>, frontendKey: string): number {
  const main = Number(row[frontendKey]) || 0;
  const aliases = ROULETTE_BACKEND_KEY_ALIASES[frontendKey];
  if (!aliases) return main;
  const fromAliases = aliases.reduce((sum, k) => sum + (Number(row[k]) || 0), 0);
  return main + fromAliases;
}

const CONTENT_TABS = [
  '튜브낚시',
  '뜰채 낚시',
  '통발 수리',
  '통발 건지기',
  '낚시운세',
  '랜덤 광고',
  '광고보기',
  '영상 광고',
  '홈 방문하기',
  '첫 방문 보상',
  '룰렛',
] as const;

/** 탭 인덱스 -> 백엔드 content_type */
const CONTENT_TAB_TO_TYPE: Record<number, string | null> = {
  0: 'tube',
  1: 'scoop',
  2: 'repair',
  3: 'pullup',
  4: 'fortune',
  5: 'shortad',
  6: 'adticket',
  7: 'longad',
  8: 'homevisit',
  9: 'welcome',
  10: 'roulette',
};

const TIME_BACKEND_TO_FRONT: Record<string, string> = {
  '0~2': '00-03',
  '3~5': '03-06',
  '6~8': '06-09',
  '9~11': '09-12',
  '12~14': '12-15',
  '15~17': '15-18',
  '18~20': '18-21',
  '21~23': '21-24',
};

const TIME_FRONT_ORDER = ['00-03', '03-06', '06-09', '09-12', '12-15', '15-18', '18-21', '21-24'];

/** 랜덤 광고 탭: 일 참여 횟수 구간 라벨 */
const COUNT_RANGE_LABELS = [
  '1-4회',
  '5-8회',
  '9-12회',
  '13-16회',
  '17-20회',
  '21-24회',
  '25-28회',
  '29-32회',
  '33-36회',
  '37-40회',
  '41-44회',
  '45-48회',
] as const;

/** 탭별 일 참여 횟수 테이블 헤더 */
const DAILY_COUNT_COLUMN_LABELS_BY_TAB: Record<number, string[]> = {
  0: ['1회', '2회', '3회', '4회', '5회', '6회', '7회', '8회'], // 튜브낚시
  1: ['1회', '2회', '3회', '4회', '5회', '6회', '7회', '8회'], // 뜰채 낚시
  2: ['1회', '2회', '3회', '4회', '5회', '6회', '7회', '8회'], // 통발 수리
  3: ['1회', '2회', '3회', '4회', '5회', '6회', '7회', '8회'], // 통발 건지기
  4: ['1회', '2회', '3회', '4회', '5회', '6회', '7회', '8회'], // 낚시운세
  5: [...COUNT_RANGE_LABELS], // 랜덤 광고
  6: ['1회', '2회', '3회', '4회', '5회', '6회', '7회', '8회', '9회', '10회', '11회', '12회'], // 광고보기 (최대 12회)
  7: ['1회', '2회', '3회', '4회', '5회', '6회', '7회', '8회'], // 영상 광고
  8: ['1회', '2회', '3회'], // 홈 방문하기
};

function formatDelta(value: number, prev: number): { text: string; isUp: boolean } {
  const diff = value - prev;
  const sign = diff >= 0 ? '+' : '';
  if (prev === 0)
    return {
      text: `${value >= 0 ? '+' : ''}${value.toLocaleString('ko-KR')} (—%)`,
      isUp: value >= 0,
    };
  const pct = Math.round((diff / prev) * 100);
  return { text: `${sign}${diff.toLocaleString('ko-KR')} (${sign}${pct}%)`, isUp: diff >= 0 };
}

export default function RouletteContent() {
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [selectedApp, setSelectedApp] = useState<string | null>('앱 전체');
  const [appOptions, setAppOptions] = useState<string[]>(['앱 전체']);
  const [userFilter, setUserFilter] = useState<UserFilterValue>('전체');
  const [contentTab, setContentTab] = useState(0);
  const [participationData, setParticipationData] = useState<Record<string, unknown> | null>(null);
  const [participationLoading, setParticipationLoading] = useState(false);
  const [participationError, setParticipationError] = useState<string | null>(null);

  const fetchFilterOptions = useCallback(() => {
    apiClient
      .get(`/api/v1/stat/pointhome/app/list`)
      .then((response) => {
        setAppOptions(['앱 전체', ...(response.data || [])]);
      })
      .catch(() => undefined);
  }, []);

  const userTypeParam = useMemo(() => {
    if (userFilter === '신규 사용자') return 'new';
    if (userFilter === '기존 사용자') return 'remained';
    return 'all';
  }, [userFilter]);

  const fetchParticipation = useCallback(() => {
    const contentType = CONTENT_TAB_TO_TYPE[contentTab];
    if (!startDate || !endDate || contentType == null) {
      setParticipationData(null);
      return;
    }
    const start = startDate.format('YYYY-MM-DD');
    const end = endDate.format('YYYY-MM-DD');
    const app = selectedApp === '앱 전체' ? 'all' : (selectedApp ?? 'all');
    setParticipationLoading(true);
    setParticipationError(null);
    apiClient
      .get<Record<string, unknown>>('/api/v1/analytics/pointhome/roulette/participation/content', {
        params: {
          start_date: start,
          end_date: end,
          content_type: contentType,
          app,
          user_type: userTypeParam,
        },
      })
      .then((res) => setParticipationData(res.data))
      .catch((err) => {
        setParticipationData(null);
        setParticipationError(err?.response?.data?.detail ?? err?.message ?? '조회 실패');
      })
      .finally(() => setParticipationLoading(false));
  }, [startDate, endDate, selectedApp, contentTab, userTypeParam]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    fetchParticipation();
  }, [fetchParticipation]);

  const userStatusChartData = useMemo((): UserStatusDatum[] | undefined => {
    const daily = participationData?.dailyUserStats as
      | Array<{ date: string; dau: number; userCount: number }>
      | undefined;
    if (!daily?.length) return undefined;
    return daily.map((row, i) => ({
      label: String(i + 1),
      dayLabel: dayjs(row.date).format('MM/DD'),
      activeUsers: row.dau ?? 0,
      participationCompletedUsers: row.userCount ?? 0,
    }));
  }, [participationData]);

  const kpiData = useMemo(() => {
    if (!participationData) return null;

    if (contentTab === 10) {
      const totalRouletteUser = Number(participationData.totalRouletteUserCount ?? 0);
      const prevTotalRouletteUser = Number(participationData.prevTotalRouletteUserCount ?? 0);
      const minRoulette = participationData.minRouletteContent as
        | { content?: string; rouletteCountRate?: number }
        | undefined;
      const maxRoulette = participationData.maxRouletteContent as
        | { content?: string; rouletteCountRate?: number }
        | undefined;
      const prevMin = participationData.prevMinRouletteContent as
        | { content?: string; rouletteCountRate?: number }
        | undefined;
      const prevMax = participationData.prevMaxRouletteContent as
        | { content?: string; rouletteCountRate?: number }
        | undefined;
      const userDelta = formatDelta(totalRouletteUser, prevTotalRouletteUser);
      return {
        activeUsers: { value: totalRouletteUser, delta: userDelta },
        transactionAndUser: {
          txn: 0,
          user: totalRouletteUser,
          txnDelta: { text: '—', isUp: false },
          userDelta,
        },
        participationRate: { value: 0, delta: { text: '—', isUp: false } },
        avgTicket: { value: 0, prev: 0 },
        maxTicket: {
          amount: maxRoulette?.content ?? 0,
          rate: maxRoulette?.rouletteCountRate ?? 0,
          prevAmount: prevMax?.content ?? 0,
          prevRate: prevMax?.rouletteCountRate ?? 0,
        },
        minTicket: {
          amount: minRoulette?.content ?? 0,
          rate: minRoulette?.rouletteCountRate ?? 0,
          prevAmount: prevMin?.content ?? 0,
          prevRate: prevMin?.rouletteCountRate ?? 0,
        },
      };
    }

    const totalDAU = Number(participationData.totalDAU ?? 0);
    const prevTotalDAU = Number(participationData.prevTotalDAU ?? 0);
    const totalUserCount = Number(participationData.totalUserCount ?? 0);
    const prevTotalUserCount = Number(participationData.prevTotalUserCount ?? 0);
    const totalTransactionCount = Number(participationData.totalTransactionCount ?? 0);
    const prevTotalTransactionCount = Number(participationData.prevTotalTransactionCount ?? 0);
    const totalTicketSum = Number(participationData.totalTicketSum ?? 0);
    const prevTotalTicketSum = Number(participationData.prevTotalTicketSum ?? 0);
    const dauDelta = formatDelta(totalDAU, prevTotalDAU);
    const txnDelta = formatDelta(totalTransactionCount, prevTotalTransactionCount);
    const userDelta = formatDelta(totalUserCount, prevTotalUserCount);
    const participationRate =
      totalDAU > 0 ? Math.round((totalUserCount / totalDAU) * 1000) / 10 : 0;
    const prevParticipationRate =
      prevTotalDAU > 0 ? Math.round((prevTotalUserCount / prevTotalDAU) * 1000) / 10 : 0;
    const participationRateDelta = formatDelta(participationRate, prevParticipationRate);
    const avgTicket =
      totalUserCount > 0 ? Math.round((totalTicketSum / totalUserCount) * 10) / 10 : 0;
    const prevAvgTicket =
      prevTotalUserCount > 0 ? Math.round((prevTotalTicketSum / prevTotalUserCount) * 10) / 10 : 0;
    const minTicket = participationData.minTicketTxnCount as
      | { amount?: number; ticketTxnCountRate?: number }
      | undefined;
    const maxTicket = participationData.maxTicketTxnCount as
      | { amount?: number; ticketTxnCountRate?: number }
      | undefined;
    const prevMin = participationData.prevMinTicketTxnCount as
      | { amount?: number; ticketTxnCountRate?: number }
      | undefined;
    const prevMax = participationData.prevMaxTicketTxnCount as
      | { amount?: number; ticketTxnCountRate?: number }
      | undefined;
    return {
      activeUsers: { value: totalDAU, delta: dauDelta },
      transactionAndUser: { txn: totalTransactionCount, user: totalUserCount, txnDelta, userDelta },
      participationRate: { value: participationRate, delta: participationRateDelta },
      avgTicket: { value: avgTicket, prev: prevAvgTicket },
      maxTicket: {
        amount: maxTicket?.amount ?? 0,
        rate: maxTicket?.ticketTxnCountRate ?? 0,
        prevAmount: prevMax?.amount ?? 0,
        prevRate: prevMax?.ticketTxnCountRate ?? 0,
      },
      minTicket: {
        amount: minTicket?.amount ?? 0,
        rate: minTicket?.ticketTxnCountRate ?? 0,
        prevAmount: prevMin?.amount ?? 0,
        prevRate: prevMin?.ticketTxnCountRate ?? 0,
      },
    };
  }, [participationData, contentTab]);

  const timeChartData = useMemo((): TimeParticipationDatum[] | undefined => {
    const bucket = participationData?.totalTicketTimeBucket as Record<string, number> | undefined;
    if (!bucket || typeof bucket !== 'object') return undefined;
    return TIME_FRONT_ORDER.map((frontKey) => {
      const backendKey = Object.keys(TIME_BACKEND_TO_FRONT).find(
        (k) => TIME_BACKEND_TO_FRONT[k] === frontKey,
      );
      const value = backendKey ? Number(bucket[backendKey] ?? 0) : 0;
      return { timeRange: frontKey, value };
    });
  }, [participationData]);

  const timeTableRows = useMemo((): TimeParticipationTableRow[] | undefined => {
    const daily = participationData?.dailyTimeBucket as Array<Record<string, unknown>> | undefined;
    if (!daily?.length) return undefined;
    return daily.map((row) => {
      const date = String(row.date ?? '');
      const intervals = TIME_FRONT_ORDER.map((frontKey) => {
        const backendKey = Object.keys(TIME_BACKEND_TO_FRONT).find(
          (k) => TIME_BACKEND_TO_FRONT[k] === frontKey,
        );
        const count = backendKey ? Number(row[backendKey] ?? 0) : 0;
        return { rate: 0, count };
      });
      const rowTotal = intervals.reduce((s, i) => s + i.count, 0);
      return {
        date,
        intervals: intervals.map((i) => ({
          rate: rowTotal > 0 ? Math.round((i.count / rowTotal) * 1000) / 10 : 0,
          count: i.count,
        })),
      };
    });
  }, [participationData]);

  const dailyChartData = useMemo((): DailyCountDatum[] | undefined => {
    const total = participationData?.totalDailyTransaction as Record<string, number> | undefined;
    if (!total || typeof total !== 'object') return undefined;
    const contentType = CONTENT_TAB_TO_TYPE[contentTab];

    if (contentType === 'shortad') {
      // 랜덤 광고: 1-4회, 5-8회, … 구간
      const byRange: Record<string, number> = {};
      COUNT_RANGE_LABELS.forEach((r) => {
        byRange[r] = 0;
      });
      Object.entries(total).forEach(([key, val]) => {
        const frontKey = key.includes('~') ? `${key.replace('~', '-')}회` : `${key}회`;
        if (COUNT_RANGE_LABELS.includes(frontKey as (typeof COUNT_RANGE_LABELS)[number]))
          byRange[frontKey] = (byRange[frontKey] ?? 0) + Number(val);
      });
      return COUNT_RANGE_LABELS.map((range) => ({ range, value: byRange[range] ?? 0 }));
    }

    if (contentTab === 6) {
      // 광고보기: 최대 12회, 1회 단위
      const labels = DAILY_COUNT_COLUMN_LABELS_BY_TAB[6];
      const keyOrder = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
      return labels.map((range, i) => ({ range, value: Number(total[keyOrder[i]] ?? 0) || 0 }));
    }

    if (contentTab === 8) {
      // 홈 방문하기: 1회, 2회, 3회
      const labels = DAILY_COUNT_COLUMN_LABELS_BY_TAB[8];
      const keyOrder = ['1', '2', '3'];
      return labels.map((range, i) => ({ range, value: Number(total[keyOrder[i]] ?? 0) || 0 }));
    }

    if (contentTab === 7) {
      // 영상 광고: 1회~8회
      const labels = DAILY_COUNT_COLUMN_LABELS_BY_TAB[7];
      const keyOrder = ['1', '2', '3', '4', '5', '6', '7', '8'];
      return labels.map((range, i) => ({ range, value: Number(total[keyOrder[i]] ?? 0) || 0 }));
    }

    // 튜브낚시(0), 뜰채 낚시(1), 통발 수리(2), 통발 건지기(3), 낚시운세(4): 1회~8회
    const labels =
      DAILY_COUNT_COLUMN_LABELS_BY_TAB[contentTab] ?? DAILY_COUNT_COLUMN_LABELS_BY_TAB[0];
    const keyOrder = ['1', '2', '3', '4', '5', '6', '7', '8'];
    return labels.map((range, i) => ({ range, value: Number(total[keyOrder[i]] ?? 0) || 0 }));
  }, [participationData, contentTab]);

  const dailyCountColumnLabels = useMemo(
    () =>
      contentTab in DAILY_COUNT_COLUMN_LABELS_BY_TAB
        ? DAILY_COUNT_COLUMN_LABELS_BY_TAB[contentTab]
        : undefined,
    [contentTab],
  );

  const dailyTableRows = useMemo((): DailyCountTableRow[] | undefined => {
    const daily = participationData?.dailyTransaction as Array<Record<string, unknown>> | undefined;
    if (!daily?.length) return undefined;
    const contentType = CONTENT_TAB_TO_TYPE[contentTab];

    if (contentType === 'shortad') {
      // 랜덤 광고: 백엔드 1~4, 5~8, … 구간 그대로 테이블에 매핑 (1-4회, 5-8회, …)
      return daily.map((row) => {
        const date = String(row.date ?? '');
        const rowData = { ...row };
        delete rowData.date;
        const byRange: Record<string, number> = {};
        COUNT_RANGE_LABELS.forEach((r) => {
          byRange[r] = 0;
        });
        Object.entries(rowData).forEach(([key, val]) => {
          const numVal = Number(val);
          if (Number.isNaN(numVal)) return;
          const frontKey = key.includes('~') ? `${key.replace('~', '-')}회` : `${key}회`;
          if (COUNT_RANGE_LABELS.includes(frontKey as (typeof COUNT_RANGE_LABELS)[number]))
            byRange[frontKey] = (byRange[frontKey] ?? 0) + numVal;
        });
        const rowTotal = Object.values(byRange).reduce((s, v) => s + v, 0);
        return {
          date,
          ranges: COUNT_RANGE_LABELS.map((range) => {
            const count = byRange[range] ?? 0;
            const rate = rowTotal > 0 ? Math.round((count / rowTotal) * 1000) / 10 : 0;
            return { rate, count };
          }),
        };
      });
    }

    if (contentTab === 6) {
      // 광고보기: 최대 12회, 1회 단위 (0회 제외)
      const keyOrder = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
      return daily.map((row) => {
        const date = String(row.date ?? '');
        const rowData = { ...row };
        delete rowData.date;
        const counts = keyOrder.map((k) => Number(rowData[k] ?? 0) || 0);
        const rowTotal = counts.reduce((s, v) => s + v, 0);
        return {
          date,
          ranges: counts.map((count) => ({
            rate: rowTotal > 0 ? Math.round((count / rowTotal) * 1000) / 10 : 0,
            count,
          })),
        };
      });
    }

    if (contentTab === 8) {
      // 홈 방문하기: 1, 2, 3 (0회 제외)
      const keyOrder = ['1', '2', '3'];
      return daily.map((row) => {
        const date = String(row.date ?? '');
        const rowData = { ...row };
        delete rowData.date;
        const counts = keyOrder.map((k) => Number(rowData[k] ?? 0) || 0);
        const rowTotal = counts.reduce((s, v) => s + v, 0);
        return {
          date,
          ranges: counts.map((count) => ({
            rate: rowTotal > 0 ? Math.round((count / rowTotal) * 1000) / 10 : 0,
            count,
          })),
        };
      });
    }

    if (contentTab === 7) {
      // 영상 광고: 1, 2, 3, 4, 5, 6, 7, 8
      const keyOrder = ['1', '2', '3', '4', '5', '6', '7', '8'];
      return daily.map((row) => {
        const date = String(row.date ?? '');
        const rowData = { ...row };
        delete rowData.date;
        const counts = keyOrder.map((k) => Number(rowData[k] ?? 0) || 0);
        const rowTotal = counts.reduce((s, v) => s + v, 0);
        return {
          date,
          ranges: counts.map((count) => ({
            rate: rowTotal > 0 ? Math.round((count / rowTotal) * 1000) / 10 : 0,
            count,
          })),
        };
      });
    }

    // 튜브낚시(0), 뜰채 낚시(1), 통발 수리(2), 통발 건지기(3), 낚시운세(4): 1회~8회 (0회 제외)
    const keyOrder = ['1', '2', '3', '4', '5', '6', '7', '8'];
    return daily.map((row) => {
      const date = String(row.date ?? '');
      const rowData = { ...row };
      delete rowData.date;
      const counts = keyOrder.map((k) => Number(rowData[k] ?? 0) || 0);
      const rowTotal = counts.reduce((s, v) => s + v, 0);
      return {
        date,
        ranges: counts.map((count) => ({
          rate: rowTotal > 0 ? Math.round((count / rowTotal) * 1000) / 10 : 0,
          count,
        })),
      };
    });
  }, [participationData, contentTab]);

  /** 뜰채 낚시: 티켓 획득 현황 (1~5티켓 비율) */
  const ticketAcquisitionData = useMemo((): TicketAcquisitionDatum[] | undefined => {
    if (contentTab !== 1 || !participationData) return undefined;
    const perAmount = participationData.totalTicketSumPerAmount as
      | Record<string, number>
      | undefined;
    if (!perAmount || typeof perAmount !== 'object') return undefined;
    const amounts = ['1', '2', '3', '4', '5'];
    const list = amounts.map((a) => ({
      name: `${a}티켓`,
      count: Number(perAmount[a] ?? 0) || 0,
      value: 0,
    }));
    const total = list.reduce((s, d) => s + d.count, 0);
    list.forEach((d) => {
      d.value = total > 0 ? Math.round((d.count / total) * 1000) / 10 : 0;
    });
    return list;
  }, [participationData, contentTab]);

  /** 룰렛 탭: dailyRoulette -> 라인 차트 (일별 × 컬럼별 참여율 %) */
  const rouletteChartData = useMemo((): RouletteTabChartDatum[] | undefined => {
    if (contentTab !== 10 || !participationData) return undefined;
    const daily = participationData.dailyRoulette as Array<Record<string, unknown>> | undefined;
    if (!Array.isArray(daily)) return undefined;
    return daily.map((row) => {
      const dateStr = String(row.date ?? '');
      const dayLabel = dateStr ? dayjs(dateStr).format('MM/DD') : '';
      const rest = { ...row };
      delete rest.date;
      const total = Object.values(rest).reduce((s: number, v) => s + (Number(v) || 0), 0);
      const entry: RouletteTabChartDatum = { dayLabel };
      ROULETTE_TAB_COLUMNS.forEach((key) => {
        const count = getRouletteCount(rest, key);
        entry[key] = total > 0 ? Math.round((count / total) * 1000) / 10 : 0;
      });
      return entry;
    });
  }, [participationData, contentTab]);

  /** 룰렛 탭: dailyRoulette -> 상세 현황 테이블 (날짜 × 컬럼별 rate/count) */
  const rouletteTableRows = useMemo((): RouletteTabTableRow[] | undefined => {
    if (contentTab !== 10 || !participationData) return undefined;
    const daily = participationData.dailyRoulette as Array<Record<string, unknown>> | undefined;
    if (!Array.isArray(daily)) return undefined;
    return daily.map((row) => {
      const dateStr = String(row.date ?? '');
      const displayDate = dateStr ? dayjs(dateStr).format('YYYY.MM.DD') : dateStr;
      const rest = { ...row };
      delete rest.date;
      const total = Object.values(rest).reduce((s: number, v) => s + (Number(v) || 0), 0);
      const cells = ROULETTE_TAB_COLUMNS.map((col) => {
        const count = getRouletteCount(rest, col);
        const rate = total > 0 ? Math.round((count / total) * 1000) / 10 : 0;
        return { rate, count };
      });
      return { date: displayDate, cells };
    });
  }, [participationData, contentTab]);

  /** 첫 방문 보상 탭: 참여 API welcome 응답 -> 룰렛 이동 CTA 비교 카드 */
  const firstVisitCta = useMemo((): WelcomeCta | null => {
    if (contentTab !== 9 || !participationData) return null;
    return (participationData.cta as WelcomeCta | undefined) ?? null;
  }, [contentTab, participationData]);

  /** 첫 방문 보상 탭: 참여 API welcome 응답 -> 미션 참여 Funnel */
  const firstVisitFunnel = useMemo((): WelcomeFunnel | null => {
    if (contentTab !== 9 || !participationData) return null;
    return (participationData.funnel as WelcomeFunnel | undefined) ?? null;
  }, [contentTab, participationData]);

  /** 첫 방문 보상 탭: 참여 API welcome 응답 -> 일별 리텐션 코호트 (전체/출석+미션/출석만) */
  const firstVisitRetention = useMemo((): WelcomeRetention | null => {
    if (contentTab !== 9 || !participationData) return null;
    return (participationData.retention as WelcomeRetention | undefined) ?? null;
  }, [contentTab, participationData]);

  /** 첫 방문 보상 탭: 참여 API welcome 응답 -> 세그먼트별 평균 참여일수(D0~D7) */
  const firstVisitAvgParticipationDays = useMemo((): WelcomeAvgParticipationDays | null => {
    if (contentTab !== 9 || !participationData) return null;
    return (
      (participationData.avgParticipationDays as WelcomeAvgParticipationDays | undefined) ?? null
    );
  }, [contentTab, participationData]);

  /** 첫 방문 보상 탭: 참여 API welcome 응답 -> 세그먼트별 개근 비율(D0~D7) */
  const firstVisitPerfectAttendanceRate = useMemo((): WelcomePerfectAttendanceRate | null => {
    if (contentTab !== 9 || !participationData) return null;
    return (
      (participationData.perfectAttendanceRate as WelcomePerfectAttendanceRate | undefined) ?? null
    );
  }, [contentTab, participationData]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* 상단 필터: 기간, 앱 */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'stretch',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
        }}
      >
        <Box
          sx={{
            flex: { xs: 'none', sm: '0 1 auto' },
            minWidth: { xs: '100%', sm: 260 },
            maxWidth: { xs: '100%', sm: 'none' },
          }}
        >
          <DateRangeSelector
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
        </Box>
        <Box
          sx={{
            flex: { xs: 'none', sm: '0 1 auto' },
            minWidth: { xs: '100%', sm: 180 },
            maxWidth: { xs: '100%', sm: 'none' },
          }}
        >
          <FilterSelectorWithApp
            selectedApp={selectedApp}
            appOptions={appOptions}
            onAppChange={(app) => setSelectedApp(app ?? '앱 전체')}
          />
        </Box>
        <Box
          sx={{
            flex: { xs: 'none', sm: '0 1 auto' },
            minWidth: { xs: '100%', sm: 160 },
            maxWidth: { xs: '100%', sm: 'none' },
          }}
        >
          <FilterSelectorUser value={userFilter} onChange={setUserFilter} />
        </Box>
      </Box>

      {/* 박스: 앱 리스트 → 구분선 -> 탭 */}
      <Box
        sx={{
          ...PAGE_CARD_SX,
          width: '100%',
          maxWidth: '100%',
          opacity: participationLoading ? 0.3 : 1,
          transition: 'opacity 0.3s ease',
        }}
      >
        {/* 앱 선택 (Chip 리스트) */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            overflowX: 'auto',
            pb: 2,
            mb: 1,
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#d1d5db', borderRadius: 4 },
            '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#9ca3af' },
          }}
        >
          {appOptions.map((name) => (
            <Chip
              key={name}
              label={name}
              onClick={() => setSelectedApp(name)}
              sx={{
                cursor: 'pointer',
                fontWeight: selectedApp === name ? 700 : 400,
                backgroundColor: selectedApp === name ? '#0044D2' : '#fff',
                color: selectedApp === name ? '#fff' : '#374151',
                border: `1px solid ${selectedApp === name ? '#0044D2' : '#e5e7eb'}`,
                '&:hover': {
                  backgroundColor: selectedApp === name ? '#0033a0' : '#f3f4f6',
                },
                height: 36,
                fontSize: '0.875rem',
              }}
            />
          ))}
        </Stack>

        <Divider variant="fullWidth" sx={{ borderColor: '#e0e0e0', mb: 2 }} />

        <Tabs
          value={contentTab}
          onChange={(_, newValue: number) => setContentTab(newValue)}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              minHeight: 36,
              color: 'rgba(0, 0, 0, 0.6)',
              fontWeight: 'normal',
              px: 2,
              py: 1,
              borderRadius: '8px',
              mr: 1,
              '&.Mui-selected': {
                backgroundColor: 'rgba(0, 68, 210, 0.1)',
                color: '#0044D2',
                fontWeight: 'bold',
              },
            },
            '& .MuiTabs-indicator': {
              display: 'none',
            },
          }}
        >
          {CONTENT_TABS.map((label) => (
            <Tab key={label} label={label} />
          ))}
        </Tabs>

        {participationLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {contentTab !== 9 && (
              <RouletteContentKpiCards
                data={kpiData}
                showTicketKpis={contentTab <= 4}
                isRouletteTab={contentTab === 10}
                contentName={CONTENT_TABS[contentTab]}
              />
            )}

            {/* 탭별 콘텐츠 영역 */}
            <Box sx={{ mt: 3 }}>
              {contentTab === 9 && (
                <TabFirstVisit
                  loading={false}
                  error={participationError}
                  cta={firstVisitCta}
                  funnel={firstVisitFunnel}
                  retention={firstVisitRetention}
                  avgParticipationDays={firstVisitAvgParticipationDays}
                  perfectAttendanceRate={firstVisitPerfectAttendanceRate}
                />
              )}
              {contentTab === 10 && (
                <TabRoulette
                  loading={false}
                  error={participationError}
                  chartData={rouletteChartData}
                  tableRows={rouletteTableRows}
                />
              )}
              {contentTab !== 9 && contentTab !== 10 && (
                <TabGeneral
                  contentTab={contentTab}
                  loading={false}
                  error={participationError}
                  userStatusChartData={userStatusChartData}
                  ticketAcquisitionData={ticketAcquisitionData}
                  timeChartData={timeChartData}
                  timeTableRows={timeTableRows}
                  dailyChartData={dailyChartData}
                  dailyCountColumnLabels={dailyCountColumnLabels}
                  dailyTableRows={dailyTableRows}
                />
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
